import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Crown, Loader2, ShieldCheck, Sparkles } from "lucide-react";

export default function Pricing() {
  const { user, openAuth } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  const subscribe = async (plan) => {
    if (!user) { openAuth("register"); return; }
    if (user.subscription_active) { toast.info("You already have an active subscription."); return; }
    setLoadingKey(plan.lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoadingKey(null);
    }
  };

  return (
    <div className="pt-24 pb-20 qs-grid-bg min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-4 py-1.5 text-xs font-semibold text-cyan-300 mb-4">
            <Sparkles size={14} /> CHOOSE YOUR EDGE
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Simple, transparent <span className="text-gradient">pricing</span>
          </h1>
          <p className="text-slate-400 mt-3 max-w-xl mx-auto">
            Start free, upgrade when you're ready to unlock Gold & Crypto signals and unlimited AI scans.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.lookup_key}
              data-testid={`pricing-card-${plan.lookup_key}`}
              className={`qs-card p-7 flex flex-col relative ${plan.popular ? "qs-glow-buy border-emerald-500/50" : ""}`}
            >
              {plan.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  MOST POPULAR
                </span>
              )}
              <div className="flex items-center gap-2">
                {plan.name === "Quant VIP" && <Crown className="text-amber-400" size={20} />}
                <h3 className="font-display text-xl font-bold">{plan.name}</h3>
              </div>
              <p className="text-sm text-slate-500 mt-1 h-10">{plan.tagline}</p>
              <div className="mt-4 mb-6">
                <span className="font-display text-4xl font-black font-mono-ticker">${plan.price}</span>
                <span className="text-slate-500 text-sm">/{plan.interval === "lifetime" ? "once" : plan.interval}</span>
              </div>
              <ul className="space-y-3 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check size={16} className="text-emerald-400 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`pricing-plan-${plan.lookup_key}-subscribe-button`}
                onClick={() => subscribe(plan)}
                disabled={loadingKey === plan.lookup_key}
                className={`mt-7 w-full rounded-full h-11 font-semibold ${
                  plan.popular
                    ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                    : "bg-slate-800 hover:bg-slate-700 text-white"
                }`}
              >
                {loadingKey === plan.lookup_key ? <Loader2 className="animate-spin" size={18} /> : `Get ${plan.name}`}
              </Button>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mt-10">
          <ShieldCheck size={16} className="text-emerald-400" /> Secure checkout by Stripe · Test mode (card 4242 4242 4242 4242)
        </div>
      </div>
    </div>
  );
}
